<template>
    <div id="VehicleSelect" v-if="searchParams">
        <progress-header />

        <div class="container mt-4">
            <div class="row justify-content-center pt-4" v-if="isLoading">
                <div class="spinner-border" role="status">
                    <span class="sr-only">{{ $t('loading') }}...</span>
                </div>
            </div>

            <div class="text-center" v-else-if="travels.length === 0">
                {{ $t('taxi.pages.vehicles.no_transfers_found') }}
            </div>

            <div class="row" v-if="travels.length > 0 && travels[0].is_short_time">
                <div class="col-12 col-lg-9">
                    <div class="alert alert-warning text-center" v-html="$t('taxi.pages.vehicles.short_time_st')" />
                </div>
            </div>

            <div class="row" v-if="!isLoading && travels.length > 0">
                <div class="col-12 col-lg-9 mb-2">
                    <div class="card booking-form-card shadow-sm">
                        <div class="card-header">
                            <span class="icon position-pin-icon" />

                            {{ searchParams.start_place }}
                            <font-awesome-icon icon="long-arrow-alt-right" class="in-text-icon" />
                            {{ searchParams.end_place }}
                        </div>

                        <div
                            class="row py-4 border-bottom travel-result"
                            :class="{'is-eco': travel.vehicle.is_eco}"
                            v-for="travel in travels"
                            :key="travel.id"
                        >
                            <div class="col-6 col-md-4">
                                <img src="/images/icon-car-blue.svg" :alt="travel.vehicle.name" class="img-fluid vehicle-image loading" v-lazy="travel.vehicle.photo_id">
                            </div>

                            <div class="col-6 col-md-4 px-sm-0">
                                <div class="vehicle-name" :class="{'is-eco': travel.vehicle.is_eco}">
                                    {{ travel.vehicle.brand }}
                                    {{ travel.vehicle.name }}
                                </div>

                                <div class="d-none d-md-block" v-if="travel.vehicle.description">
                                    <div>
                                        {{ travel.vehicle.description }}
                                    </div>
                                </div>
                            </div>

                            <div class="col-6 mt-2 d-block d-md-none" v-if="travel.vehicle.description">
                                <div>
                                    {{ travel.vehicle.description }}
                                </div>
                            </div>

                            <div class="col-6 col-md-4 text-right">
                                <div class="text-lg text-blue-600 mt-2 gross-price">
                                    <template v-if="travel.promo_price && travel.gross_price != travel.promo_price.gross_price">
                                        <del class="d-block">{{ formatPrice(travel.gross_price, travel.currency) }}</del>
                                        {{ formatPrice(travel.promo_price.gross_price, travel.currency) }}
                                    </template>

                                    <template v-else>
                                        {{ formatPrice(travel.gross_price, travel.currency) }}
                                    </template>
                                </div>

                                <div class="text-sm text-muted">
                                    <div class="net-price">
                                        <template v-if="travel.promo_price && travel.net_price != travel.promo_price.net_price">
                                            <del class="d-block" v-if="travel.promo_price && travel.net_price != travel.promo_price.net_price">{{ formatPrice(travel.net_price, travel.currency) }}</del>
                                            {{ formatPrice(travel.promo_price.net_price, travel.currency) }} {{ $t('net') }}
                                        </template>

                                        <template v-else>
                                            {{ formatPrice(travel.net_price, travel.currency) }} {{ $t('net') }}
                                        </template>
                                    </div>
                                </div>

                                <button class="btn btn-primary select-button" @click="handlePickTravel(travel)">
                                    {{ $t('select') }}
                                </button>

                                <div class="mt-4">
                                    <number-box :number="travel.vehicle.passengers_number_max" icon="seat" class="d-inline-block" :tooltip="$t('passengers_amount')" />
                                    <number-box :number="travel.vehicle.hand_luggage_amount" icon="bag-personal" class="d-inline-block" :tooltip="$t('baggage_hand')" />
                                    <number-box :number="travel.vehicle.checked_baggage_amount" icon="bag-suitcase" class="d-inline-block" :tooltip="$t('baggage_register')" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <v-dialog />

                <div class="col-12 col-lg-3" v-if="travels.length > 0">
                    <the-cart />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import { mapGetters } from 'vuex';

    import TheCart from './components/Cart/TheCart';
    import ProgressHeader from './components/ProgressHeader';
    import NumberBox from '@components/NumberBox';
    import {trackGA} from '@utilities/tracking';
    import backofficeRepository from '@repositories/BackofficeRepository';
    import backofficeMapsRepository from '@repositories/BackofficeMapsRepository';
    import {getCurrency} from '@utilities/helpers';

    export default {
        components: {
            TheCart,
            ProgressHeader,
            NumberBox,
        },
        directives: {
            lazy: {
                inserted: (el, binding) => {
                    function loadImage() {
                        const xhr = new XMLHttpRequest();
                        xhr.responseType = 'blob'; //so you can access the response like a normal URL
                        xhr.onreadystatechange = function () {
                            if (xhr.readyState == XMLHttpRequest.DONE && xhr.status == 200) {
                                el.src = URL.createObjectURL(xhr.response); //src set to the blob
                                el.classList.remove('loading');
                            }
                        };
                        xhr.open('GET', window.APP.backoffice.url + '/api/files/view/' + binding.value, true);
                        xhr.send();
                    }

                    function handleIntersect(entries, observer) {
                        entries.forEach(entry => {
                            if (entry.isIntersecting) {
                                loadImage();
                                observer.unobserve(el);
                            }
                        });
                    }

                    const observer = new IntersectionObserver(handleIntersect);
                    observer.observe(el);
                },
            },
        },
        data() {
            return {
                isLoading: false,
                currentLanguage: window.APP.locale,
                travels: [],
            };
        },

        computed: {
            ...mapGetters({
                searchParams: 'familyAirportTaxi/searchParameters',
                passengersNumber: 'familyAirportTaxi/passengersNumber',
            }),
        },

        created() {
            trackGA('checkout_progress', {
                checkout_step: 2,
                checkout_option: window.location.pathname,
            });

            if (!this.searchParams) {
                this.$router.push({ name: 'smart-taxi.index' });
            } else {
                this.loadVehicles(this.searchParams);
            }
        },

        methods: {

            loadVehicles(searchParameters) {
                this.isLoading = true;

                //get coords from start_place_id and end_place_id
                const promises = [backofficeRepository.searchMapPlaceDetails(searchParameters.start_place_id), backofficeRepository.searchMapPlaceDetails(searchParameters.end_place_id)];
                Promise.all(promises).then((responses) => {
                    const start_place_coords = {lat: responses[0].locationLatitude, lng: responses[0].locationLongitude};
                    const end_place_coords = {lat: responses[1].locationLatitude, lng: responses[1].locationLongitude};

                    backofficeRepository.findPreferredVehiclesPrices('FAMILY_AIRPORT_TRANSPORT', getCurrency(), start_place_coords, end_place_coords, searchParameters.departure_date + ' ' + searchParameters.departure_time).then((response) => {
                        this.travels = backofficeMapsRepository.BOVehiclesToTravels(response.vehicles, searchParameters, searchParameters.passengers[0].quantity+searchParameters.passengers[1].quantity)
                            .sort((a, b) => a.vehicle.name.localeCompare(b.vehicle.name))
                            .sort((a, b) => a.gross_price - b.gross_price);
                    }).finally(() => {
                        this.isLoading = false;
                    });
                });
            },

            handlePickTravel(travel) {
                if (travel.vehicle.is_eco) {
                    this.$modal.show('dialog', {
                        //title: '',
                        text: this.$t('taxi.pages.vehicles.eco_vehicle_confirmation', {offerType: this.$t('offer_types.family_airport_taxi')}),
                        buttons: [
                            {
                                title: this.$t('cancel'),
                            },
                            {
                                title: this.$t('ok'),
                                handler: () => {
                                    this.chooseTravel(travel);
                                },
                            },
                        ],
                    });
                    return;
                }

                this.chooseTravel(travel);
            },

            chooseTravel(travel) {
                this.$store.dispatch('familyAirportTaxi/setTravel', travel).then(() => {
                    this.$router.push({ name: 'smart-taxi.passengers' });
                });
            },
        },
    };
</script>

<style scoped>
.vehicle-image.loading{
    width: 80%;
}
</style>
